import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  BadgeCheck,
  CalendarClock,
  ChevronRight,
  CreditCard,
  Download,
  Heart,
  Headphones,
  Info,
  ImagePlus,
  Laptop,
  Loader2,
  Lock,
  Minus,
  Package,
  PackageCheck,
  Plus,
  RefreshCcw,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Users,
  Zap,
} from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { Stars } from "@/components/ReviewsSection";
import HelpfulButton from "@/components/HelpfulButton";
import Countdown from "@/components/Countdown";
import PaymentBadges from "@/components/PaymentBadges";
import { useProduct } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDate } from "@/lib/format";
import { useSeo } from "@/lib/seo";
import RichText, { stripRichText, RichTextBlocks } from "@/components/RichText";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ProductDetail() {
  const { slug } = useParams();
  const { data, isLoading, refetch } = useProduct(slug);
  const { addToCart, toggleFavorite, isFavorite, currency } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewPhoto, setReviewPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [fieldValues, setFieldValues] = useState({});

  const product = data?.product;
  const requiredFields = product?.required_fields || [];

  const [digitalOrder, setDigitalOrder] = useState(null);
  useEffect(() => {
    if (!user || !product?.id) {
      setDigitalOrder(null);
      return;
    }
    let active = true;
    api.get(`/my/delivery/by-product/${product.id}`)
      .then(({ data: payload }) => active && setDigitalOrder(payload))
      .catch(() => {});
    return () => { active = false; };
  }, [user, product?.id]);

  useEffect(() => {
    if (!user || !product?.id) {
      setEligibility(null);
      return;
    }
    api.get(`/reviews/eligibility?product_id=${product.id}`)
      .then(({ data: elig }) => setEligibility(elig))
      .catch(() => setEligibility(null));
  }, [user, product?.id]);

  const validateFields = () => {
    for (const spec of requiredFields) {
      const val = fieldValues[spec.key];
      const empty = Array.isArray(val) ? val.length === 0 : !String(val ?? "").trim();
      if (spec.required !== false && empty) {
        toast.error(`Veuillez renseigner « ${spec.label} » avant de commander`);
        document.getElementById(`field-${spec.key}`)?.focus();
        return false;
      }
    }
    return true;
  };

  useSeo({
    title: product ? `${product.name} — ISMO-TECH` : "Produit — ISMO-TECH",
    description: stripRichText(product?.short_description),
    image: product?.image_url,
    jsonLd: product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: [product.image_url],
          description: stripRichText(product.short_description),
          brand: { "@type": "Brand", name: product.brand || "ISMO-TECH" },
          offers: {
            "@type": "Offer",
            price: product.price,
            priceCurrency: "XOF",
            availability: product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: window.location.href,
          },
          aggregateRating: product.reviews_count
            ? { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews_count }
            : undefined,
        }
      : null,
  });

  if (isLoading) {
    return (
      <div className="container-it flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-it py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Produit introuvable</h1>
        <Link to="/boutique" className="mt-5 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">
          Retour à la boutique
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(product.id);
  const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='400'%20height='400'%3E%3Crect%20width='400'%20height='400'%20fill='%23EEF2F7'/%3E%3Ctext%20x='200'%20y='210'%20font-family='Arial'%20font-size='24'%20font-weight='bold'%20fill='%2394A3B8'%20text-anchor='middle'%3EISMO-TECH%3C/text%3E%3C/svg%3E";
  const specs = [
    { icon: BadgeCheck, label: "Type de licence", value: product.license_type },
    { icon: CalendarClock, label: "Durée", value: product.duration },
    { icon: Users, label: "Appareils", value: product.devices ? `${product.devices} appareil${product.devices > 1 ? "s" : ""}` : "—" },
    { icon: Laptop, label: "Compatibilité", value: (product.compatibility || []).join(", ") || "—" },
    { icon: Zap, label: "Livraison", value: product.delivery_mode === "license_key" ? "Clé après validation" : "Livraison après validation" },
    { icon: RefreshCcw, label: "Garantie", value: product.warranty },
    ...(product.custom_specs || [])
      .filter((s) => s.label)
      .map((s) => ({ icon: Info, label: s.label, value: s.value || "—" })),
  ];

  const submitReview = async (event) => {
    event.preventDefault();
    if (!user) {
      toast.error("Connectez-vous pour laisser un avis");
      navigate("/connexion");
      return;
    }
    setSending(true);
    try {
      let photo_path = "";
      if (reviewPhoto) {
        const fd = new FormData();
        fd.append("file", reviewPhoto);
        const { data: up } = await api.post("/reviews/photo", fd);
        photo_path = up.path;
      }
      await api.post("/reviews", { product_id: product.id, rating: reviewForm.rating, comment: reviewForm.comment, photo_path });
      toast.success("Merci ! Votre avis a été publié.");
      setReviewForm({ rating: 5, comment: "" });
      setReviewPhoto(null);
      setEligibility({ can_review: false, already_reviewed: true, reason: "reviewed" });
      refetch();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSending(false);
    }
  };

  const handleAdd = () => {
    if (!validateFields()) return;
    addToCart(product, quantity, requiredFields.length ? { ...fieldValues } : null);
  };

  const buyNow = () => {
    if (!validateFields()) return;
    addToCart(product, quantity, requiredFields.length ? { ...fieldValues } : null);
    if (!user) {
      toast.error("Connectez-vous pour finaliser votre commande");
      navigate("/connexion", { state: { from: "/commander" } });
      return;
    }
    navigate("/commander");
  };

  return (
    <div className="pb-10">
      <div className="container-it py-5">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
          <Link to="/" className="hover:text-brand">Accueil</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/boutique" className="hover:text-brand">Boutique</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/boutique?categorie=${product.category_slug}`} className="hover:text-brand">
            {data.category?.name || product.category_slug}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="truncate font-semibold text-ink">{product.name}</span>
        </nav>
      </div>

      <div className="container-it grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:gap-14 lg:items-start">
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative overflow-hidden rounded-2xl border border-line bg-slate-50 p-6 sm:p-10">
            {product.discount_percent > 0 && (
              <span className="absolute left-4 top-4 rounded-full bg-rose-500 px-3 py-1.5 text-xs font-black text-white">
                -{product.discount_percent}%
              </span>
            )}
            <img
              src={product.image_url || PLACEHOLDER_IMG}
              alt={product.name}
              className="mx-auto aspect-square w-full max-w-[420px] object-contain"
            />
          </div>
          <div className="mt-4 hidden lg:block">
            <PaymentBadges size="sm" />
          </div>

          {/* Bloc de réassurance — comble l'espace de la colonne gauche */}
          <div className="mt-4 hidden rounded-2xl border border-line bg-white p-5 lg:block" data-testid="product-reassurance">
            <p className="label-eyebrow mb-3">Pourquoi commander sur ISMO-TECH</p>
            <ul className="space-y-3.5">
              {[
                { icon: ShieldCheck, color: "#0052FF", title: "Paiement 100% sécurisé",
                  text: "Vos transactions sont protégées et vérifiées manuellement." },
                { icon: Zap, color: "#F59E0B", title: "Traitement rapide",
                  text: "Votre commande est prise en charge en priorité par notre équipe." },
                { icon: PackageCheck, color: "#10B981", title: "Livraison numérique",
                  text: product.delivery_mode === "license_key"
                    ? "Clé d'activation débloquée dès la confirmation du paiement."
                    : "Fichiers, liens et instructions débloqués dès la confirmation du paiement." },
                { icon: RefreshCcw, color: "#6366F1", title: "Garantie incluse",
                  text: product.warranty || "Produit garanti — assistance en cas de souci." },
                { icon: Headphones, color: "#EC4899", title: "Support 7j/7",
                  text: "Une équipe disponible pour vous accompagner à chaque étape." },
              ].map(({ icon: Icon, color, title, text }) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${color}14` }}>
                    <Icon className="h-4 w-4" style={{ color }} />
                  </span>
                  <span>
                    <span className="block text-[13px] font-extrabold text-ink">{title}</span>
                    <span className="block text-[11.5px] leading-snug text-slate-500">{text}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-4 border-t border-line pt-3">
              <p className="mb-2 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                Moyens de paiement acceptés
              </p>
              <div className="flex flex-wrap gap-1.5">
                {["Orange Money", "MTN MoMo", "Moov Money", "Wave", "Carte bancaire"].map((m) => (
                  <span key={m} className="rounded-md border border-line bg-slate-50 px-2 py-1 text-[10.5px] font-bold text-slate-600">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Vendeur */}
          {product.vendor_slug && (
            <Link
              to={`/boutique-vendeur/${product.vendor_slug}`}
              data-testid="product-vendor-card"
              className="mt-4 hidden items-center gap-3 rounded-2xl border border-line bg-white p-4 transition-colors duration-200 hover:border-brand lg:flex"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-black text-white">
                {(product.vendor_name || "V")[0]}
              </span>
              <span className="min-w-0">
                <span className="block text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  Vendu par
                </span>
                <span className="block truncate text-sm font-extrabold text-ink">{product.vendor_name}</span>
                <span className="text-[11px] font-bold text-brand">Voir la boutique →</span>
              </span>
            </Link>
          )}
        </div>

        <div>
          <p className="label-eyebrow">{product.license_type}</p>
          <h1 className="mt-2 font-display text-2xl font-extrabold leading-tight text-ink sm:text-4xl" data-testid="product-title">
            {product.name}
          </h1>
          {product.vendor_slug && (
            <p className="mt-1 text-sm text-slate-500" data-testid="product-vendor">
              Vendu par{" "}
              <Link to={`/boutique-vendeur/${product.vendor_slug}`} className="font-bold text-brand hover:underline">
                {product.vendor_name}
              </Link>
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            <span className="flex items-center gap-2">
              {product.reviews_count > 0 ? (
                <>
                  <Stars rating={product.rating || 5} size="h-4 w-4" />
                  <span className="text-xs text-slate-500">
                    {product.rating}/5 · {data.reviews.length} avis
                  </span>
                </>
              ) : (
                <span className="text-xs text-slate-500">Aucun avis pour le moment</span>
              )}
            </span>
            {product.service_label && (
              <span
                data-testid="product-service-badge"
                className="rounded-md bg-amber-500 px-2.5 py-1 text-xs font-bold text-white"
              >
                {product.service_label}
              </span>
            )}
            <span className="rounded-md border border-line bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
              Numérique
            </span>
            <span
              data-testid="product-availability"
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${
                product.in_stock
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-rose-200 bg-rose-50 text-rose-600"
              }`}
            >
              <Package className="h-3.5 w-3.5" />
              {product.in_stock ? `En stock (${product.stock})` : "Rupture de stock"}
            </span>
          </div>

          {product.delivery_time && (
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-600" data-testid="product-delivery-time">
              <Truck className="h-4 w-4 text-slate-400" />
              Délai de livraison : <span className="font-semibold text-ink">{product.delivery_time}</span>
            </p>
          )}

          <p className="mt-5 text-[15px] leading-relaxed text-slate-600" data-testid="product-short-description">
            <RichText text={product.short_description} />
          </p>

          <div className="mt-6 rounded-2xl border border-line bg-white p-5">
            <div className="flex flex-wrap items-end gap-3">
              <span className="font-display text-3xl font-black text-ink sm:text-4xl" data-testid="product-detail-price">
                {formatPrice(product.price, currency)}
              </span>
              {product.old_price > product.price && (
                <span className="text-lg text-slate-400 line-through">
                  {formatPrice(product.old_price, currency)}
                </span>
              )}
              {product.discount_percent > 0 && (
                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600">
                  Économisez {formatPrice(product.old_price - product.price, currency)}
                </span>
              )}
            </div>

            {product.promo_ends_at && (
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Fin de l'offre</span>
                <Countdown endsAt={product.promo_ends_at} testId="product-countdown" />
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center rounded-full border border-line">
                <button
                  data-testid="qty-minus"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-200 hover:text-brand"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span data-testid="qty-value" className="w-8 text-center font-bold text-ink">{quantity}</span>
                <button
                  data-testid="qty-plus"
                  onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                  className="flex h-11 w-11 items-center justify-center text-ink transition-colors duration-200 hover:text-brand"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                data-testid="product-favorite-btn"
                onClick={() => toggleFavorite(product)}
                className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors duration-200 ${
                  favorite ? "border-rose-200 bg-rose-50 text-rose-500" : "border-line text-slate-400 hover:text-rose-500"
                }`}
              >
                <Heart className="h-5 w-5" fill={favorite ? "currentColor" : "none"} />
              </button>
            </div>

            {requiredFields.length > 0 && (
              <div className="mt-5 space-y-3 border-t border-line pt-4" data-testid="product-required-fields">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Informations requises pour l'activation
                </p>
                {requiredFields.map((spec) => (
                  <div key={spec.key} className="block">
                    <span className="mb-1 block text-xs font-bold text-ink">
                      {spec.label} {spec.required !== false && <span className="text-rose-500">*</span>}
                    </span>
                    {spec.type === "textarea" ? (
                      <textarea
                        id={`field-${spec.key}`}
                        data-testid={`product-field-${spec.key}`}
                        rows={3}
                        required={spec.required !== false}
                        placeholder={spec.placeholder || spec.label}
                        value={fieldValues[spec.key] || ""}
                        onChange={(e) => setFieldValues((v) => ({ ...v, [spec.key]: e.target.value }))}
                        className="w-full rounded-xl border border-line p-3 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                      />
                    ) : spec.type === "select" ? (
                      <select
                        id={`field-${spec.key}`}
                        data-testid={`product-field-${spec.key}`}
                        required={spec.required !== false}
                        value={fieldValues[spec.key] || ""}
                        onChange={(e) => setFieldValues((v) => ({ ...v, [spec.key]: e.target.value }))}
                        className="h-11 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                      >
                        <option value="">{spec.placeholder || "Choisir une option"}</option>
                        {(spec.options || []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : spec.type === "radio" ? (
                      <div id={`field-${spec.key}`} data-testid={`product-field-${spec.key}`} className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
                        {(spec.options || []).map((opt) => (
                          <label key={opt} className="flex items-center gap-2 text-sm text-ink">
                            <input
                              type="radio"
                              name={`field-${spec.key}`}
                              value={opt}
                              checked={fieldValues[spec.key] === opt}
                              onChange={() => setFieldValues((v) => ({ ...v, [spec.key]: opt }))}
                              className="h-4 w-4 border-slate-300 text-brand focus:ring-brand"
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    ) : spec.type === "checkbox" ? (
                      <div id={`field-${spec.key}`} data-testid={`product-field-${spec.key}`} className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
                        {(spec.options || []).map((opt) => {
                          const arr = Array.isArray(fieldValues[spec.key]) ? fieldValues[spec.key] : [];
                          return (
                            <label key={opt} className="flex items-center gap-2 text-sm text-ink">
                              <input
                                type="checkbox"
                                value={opt}
                                checked={arr.includes(opt)}
                                onChange={(e) =>
                                  setFieldValues((v) => {
                                    const cur = Array.isArray(v[spec.key]) ? v[spec.key] : [];
                                    return { ...v, [spec.key]: e.target.checked ? [...cur, opt] : cur.filter((o) => o !== opt) };
                                  })
                                }
                                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
                              />
                              {opt}
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <input
                        id={`field-${spec.key}`}
                        data-testid={`product-field-${spec.key}`}
                        type={spec.type || "text"}
                        required={spec.required !== false}
                        placeholder={spec.placeholder || spec.label}
                        value={fieldValues[spec.key] || ""}
                        onChange={(e) => setFieldValues((v) => ({ ...v, [spec.key]: e.target.value }))}
                        className="h-11 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                      />
                    )}
                    {spec.help && <span className="mt-1 block text-[11px] text-slate-400">{spec.help}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                data-testid="buy-now-btn"
                disabled={!product.in_stock}
                onClick={buyNow}
                className="btn-glow rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                Acheter maintenant
              </button>
              <button
                data-testid="add-to-cart-btn"
                disabled={!product.in_stock}
                onClick={handleAdd}
                className="flex items-center justify-center gap-2 rounded-full border-2 border-ink px-6 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
              >
                <ShoppingCart className="h-4 w-4" /> Ajouter au panier
              </button>
            </div>

            <div className="mt-4 overflow-hidden rounded-2xl border border-brand/15" data-testid="product-postpayment-info">
              <div className="flex items-center gap-2 bg-brand px-4 py-2.5">
                <Info className="h-4 w-4 shrink-0 text-white" />
                <p className="text-xs font-extrabold uppercase tracking-wide text-white">
                  Important — Après votre paiement
                </p>
              </div>
              <div className="space-y-3 bg-brand-soft/50 p-4">
                <p className="text-xs leading-relaxed text-slate-700">
                  Les détails de votre commande, votre livraison{" "}
                  ({product.delivery_mode === "license_key" ? "clé d'activation" : "lien, fichiers ou instructions"})
                  ainsi que votre <span className="font-bold">facture PDF</span> seront disponibles dans votre espace
                  client, menu{" "}
                  <Link to="/compte/commandes" className="font-bold text-brand underline">« Mes commandes »</Link>,
                  dès que notre équipe aura validé votre commande.
                </p>
                <div className="flex flex-wrap items-center gap-1.5 text-[10.5px] font-bold text-slate-600">
                  <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm">
                    <CreditCard className="h-3 w-3 text-brand" /> 1. Paiement
                  </span>
                  <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                  <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm">
                    <ShieldCheck className="h-3 w-3 text-amber-500" /> 2. Validation par notre équipe
                  </span>
                  <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" />
                  <span className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1 shadow-sm">
                    <PackageCheck className="h-3 w-3 text-emerald-600" /> 3. Livraison dans « Mes commandes »
                  </span>
                </div>
              </div>
            </div>

            {product.product_type ? (() => {
              const st = digitalOrder?.status;
              const released = st === "released";
              const pending = st && st !== "released";
              return (
                <div
                  className={`mt-4 overflow-hidden rounded-2xl border ${released ? "border-emerald-200" : pending ? "border-amber-200" : "border-slate-200"}`}
                  data-testid="product-digital-download-card"
                >
                  <div className="flex items-center gap-2 bg-slate-900 px-4 py-2.5">
                    <Download className="h-4 w-4 shrink-0 text-white" />
                    <p className="text-xs font-extrabold uppercase tracking-wide text-white">
                      📥 Téléchargement du produit
                    </p>
                  </div>
                  {released ? (
                    <div className="bg-emerald-50 p-4" data-testid="download-state-available">
                      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-emerald-700">
                        <PackageCheck className="h-4 w-4" /> ✅ Téléchargement disponible
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                        Votre commande #{digitalOrder.order_number} a été validée. Vos fichiers, liens et instructions sont disponibles.
                      </p>
                      <Link
                        to={`/compte/commandes/${digitalOrder.order_id}/telechargement`}
                        data-testid="product-download-btn"
                        className="mt-3 flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-emerald-700"
                      >
                        <Download className="h-4 w-4" /> Télécharger maintenant
                      </Link>
                    </div>
                  ) : pending ? (
                    <div className="bg-amber-50 p-4" data-testid="download-state-pending">
                      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-amber-700">
                        <Lock className="h-4 w-4" /> 🔒 Téléchargement en attente
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-amber-700">
                        Votre commande #{digitalOrder.order_number} est en cours de vérification. Le téléchargement sera disponible après validation du paiement.
                      </p>
                      <button
                        type="button" disabled data-testid="product-download-btn"
                        className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-slate-300 px-6 py-3 text-sm font-bold text-slate-500"
                      >
                        <Lock className="h-4 w-4" /> Télécharger
                      </button>
                    </div>
                  ) : (
                    <div className="bg-slate-50 p-4" data-testid="download-state-locked">
                      <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-slate-600">
                        <Lock className="h-4 w-4" /> 🔒 Téléchargement verrouillé
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">
                        Achetez ce produit et attendez la confirmation de votre paiement pour accéder au téléchargement.
                      </p>
                      <button
                        type="button" disabled data-testid="product-download-btn"
                        className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-slate-300 px-6 py-3 text-sm font-bold text-slate-500"
                      >
                        <Lock className="h-4 w-4" /> Télécharger
                      </button>
                    </div>
                  )}
                </div>
              );
            })() : null}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {specs.map(({ icon: Icon, label, value }) => (
              <div key={label} className="rounded-xl border border-line bg-white p-3.5">
                <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  <Icon className="h-3.5 w-3.5" /> {label}
                </p>
                <p className="mt-1.5 text-[13px] font-semibold leading-snug text-ink">{value}</p>
              </div>
            ))}
          </div>

          {product.license_origin && (
            <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-[11.5px] leading-snug text-amber-800">
              <span className="font-bold">Provenance de la licence : </span>
              <RichText text={product.license_origin} />
            </p>
          )}
        </div>
      </div>

      <div className="container-it mt-12">
        <Tabs defaultValue="description">
          <TabsList className="h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
            {[
              ["description", "Description"],
              ["features", "Caractéristiques"],
              ["delivery", "Livraison & garantie"],
              ["conditions", "Conditions d'utilisation"],
              ["reviews", `Avis (${data.reviews.length})`],
            ].map(([value, label]) => (
              <TabsTrigger
                key={value}
                value={value}
                data-testid={`tab-${value}`}
                className="rounded-full border border-line px-5 py-2.5 text-sm font-bold data-[state=active]:border-brand data-[state=active]:bg-brand data-[state=active]:text-white"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="description" className="mt-6 rounded-2xl border border-line bg-white p-6 sm:p-8">
            <div className="text-[15px] leading-relaxed text-slate-600" data-testid="product-description-text">
              <RichTextBlocks text={product.description} />
            </div>
          </TabsContent>

          <TabsContent value="features" className="mt-6 rounded-2xl border border-line bg-white p-6 sm:p-8">
            <ul className="grid gap-3 sm:grid-cols-2">
              {(product.features || []).map((feature) => (
                <li key={feature} className="flex items-start gap-2.5 text-sm text-slate-600">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <RichText text={feature} />
                </li>
              ))}
            </ul>
          </TabsContent>

          <TabsContent value="delivery" className="mt-6 rounded-2xl border border-line bg-white p-6 sm:p-8">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="label-eyebrow mb-2">Mode de livraison</p>
                <p className="text-sm leading-relaxed text-slate-600">
                  <RichText text={product.delivery_instructions} />
                </p>
              </div>
              <div>
                <p className="label-eyebrow mb-2">Garantie</p>
                <p className="text-sm leading-relaxed text-slate-600">{product.warranty}</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="conditions" className="mt-6 rounded-2xl border border-line bg-white p-6 sm:p-8">
            <div className="text-sm leading-relaxed text-slate-600">
              <RichTextBlocks text={product.conditions} />
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6 rounded-2xl border border-line bg-white p-6 sm:p-8">
            <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
              <div className="space-y-4">
                {data.reviews.length === 0 && (
                  <p className="text-sm text-slate-500">Aucun avis pour ce produit pour le moment.</p>
                )}
                {data.reviews.map((review) => (
                  <div key={review.id} className="rounded-xl border border-line p-4" data-testid={`product-review-${review.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">
                          {review.author_name?.[0]}
                        </span>
                        <span>
                          <span className="block text-sm font-bold text-ink">{review.author_name}</span>
                          <Stars rating={review.rating} />
                        </span>
                      </span>
                      <span className="text-xs text-slate-400">{formatDate(review.created_at)}</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{review.comment}</p>
                    {review.photo_url && (
                      <a href={fileUrl(review.photo_url)} target="_blank" rel="noreferrer" data-testid={`review-photo-${review.id}`}>
                        <img
                          src={fileUrl(review.photo_url)}
                          alt="Photo du client"
                          loading="lazy"
                          className="mt-3 h-24 w-24 rounded-lg border border-line object-cover transition-transform duration-200 hover:scale-105"
                        />
                      </a>
                    )}
                    {review.admin_reply && (
                      <div className="mt-3 rounded-lg border-l-2 border-brand bg-brand-soft p-3" data-testid={`review-admin-reply-${review.id}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-brand">Réponse de ISMO-TECH</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{review.admin_reply}</p>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-2 border-t border-line pt-3">
                      {review.verified_purchase ? (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                          <BadgeCheck className="h-3.5 w-3.5" /> Achat vérifié
                        </span>
                      ) : (
                        <span />
                      )}
                      <HelpfulButton review={review} />
                    </div>
                  </div>
                ))}
              </div>

              {user && eligibility?.already_reviewed ? (
                <div className="flex h-fit items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="review-already-done">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800">Avis publié — merci !</p>
                    <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                      Vous avez déjà partagé votre expérience sur ce produit.
                    </p>
                  </div>
                </div>
              ) : user && eligibility && !eligibility.can_review ? (
                <div className="h-fit rounded-xl border border-line bg-surface p-5" data-testid="review-not-eligible">
                  <p className="font-display text-base font-bold text-ink">Laisser un avis</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">
                    Les avis sont réservés aux clients ayant reçu ce produit. Commandez-le, puis partagez
                    votre expérience après la livraison depuis « Mes commandes » ou cette page.
                  </p>
                </div>
              ) : (
              <form onSubmit={submitReview} className="rounded-xl border border-line bg-surface p-5" data-testid="review-form">
                <p className="font-display text-base font-bold text-ink">Laisser un avis</p>
                <div className="mt-3 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`review-star-${n}`}
                      onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                      className={`text-2xl leading-none transition-colors duration-200 ${
                        n <= reviewForm.rating ? "text-amber-400" : "text-slate-300"
                      }`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="review-comment"
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
                  required
                  minLength={3}
                  rows={4}
                  placeholder="Partagez votre expérience..."
                  className="mt-3 w-full rounded-lg border border-line p-3 text-sm focus:border-brand focus:outline-none"
                />
                <div className="mt-2">
                  {reviewPhoto ? (
                    <span className="flex items-center gap-2" data-testid="review-photo-preview">
                      <img src={URL.createObjectURL(reviewPhoto)} alt="Aperçu" className="h-12 w-12 rounded-lg border border-line object-cover" />
                      <span className="max-w-[130px] truncate text-[11px] text-slate-500">{reviewPhoto.name}</span>
                      <button
                        type="button"
                        data-testid="review-photo-remove"
                        onClick={() => setReviewPhoto(null)}
                        className="text-[11px] font-bold text-rose-500 hover:underline"
                      >
                        Retirer
                      </button>
                    </span>
                  ) : (
                    <label className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-[11px] font-bold text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand">
                      <ImagePlus className="h-3.5 w-3.5" />
                      Ajouter une photo (facultatif)
                      <input
                        type="file"
                        data-testid="review-photo-input"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f && f.size > 5 * 1024 * 1024) return toast.error("Image trop lourde (max 5 Mo)");
                          if (f) setReviewPhoto(f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  )}
                </div>
                <button
                  type="submit"
                  data-testid="review-submit"
                  disabled={sending}
                  className="mt-3 w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60"
                >
                  {sending ? "Envoi..." : "Publier mon avis"}
                </button>
                {!user ? (
                  <p className="mt-2 text-[11px] text-slate-500">
                    Vous devez être connecté pour publier un avis.
                  </p>
                ) : (
                  <p className="mt-2 text-[11px] text-slate-500">
                    Réservé aux clients ayant reçu ce produit. Votre avis est publié immédiatement.
                  </p>
                )}
              </form>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {data.related?.length > 0 && (
        <div className="container-it mt-14">
          <h2 className="mb-6 font-display text-xl font-extrabold text-ink sm:text-2xl">Produits similaires</h2>
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {data.related.map((item, i) => (
              <ProductCard key={item.id} product={item} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
